import NoConnectionIcon from "@mui/icons-material/AirplanemodeInactive";
import { Grid, Theme, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import {
  AircraftStatusPhaseOfFlight,
  IAircraftLastStatus,
  IAircraftStatusState,
  AircraftStateProperties,
} from "@vmosolutions/oc-interfaces";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { formatDateString } from "src/components/DateDisplay";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { convertMilli2DaysHrsMin } from "src/utils";
import ClimbingIcon from "./icons/airplane-climbing.svg?react";
import CruisingIcon from "./icons/airplane-cruising.svg?react";
import DescendingIcon from "./icons/airplane-descending.svg?react";
import GroundedIcon from "./icons/airplane-grounded.svg?react";
import ParkedIcon from "./icons/airplane-parked.svg?react";
import TaxiingIcon from "./icons/airplane-taxiing.svg?react";

interface IAircraftStatusProps {
  windowId: string;
  aircraftId: string;
  isSelected: boolean;
  groupColor: string;
}

interface IAnchorPos {
  left: number;
  top: number;
}

const getStateIcon = (phase: string): JSX.Element => {
  switch (phase) {
    case AircraftStatusPhaseOfFlight.stationary:
      return <ParkedIcon className="phase_icon" />;
    case AircraftStatusPhaseOfFlight.taxi:
      return <TaxiingIcon className="phase_icon" />;
    //case AircraftStatusPhaseOfFlight.takeoff:
    //  return <TakeoffIcon className="phase_icon" />;
    case AircraftStatusPhaseOfFlight.climb:
      return <ClimbingIcon className="phase_icon" />;
    case AircraftStatusPhaseOfFlight.cruise:
      return <CruisingIcon className="phase_icon" />;
    case AircraftStatusPhaseOfFlight.descent:
      return <DescendingIcon className="phase_icon" />;
    //case AircraftStatusPhaseOfFlight.landing:
    //  return <LandingIcon className="phase_icon" />;
    case AircraftStatusPhaseOfFlight.emergency:
      //TODO  Discuss appropriate icon for emergency
      return <GroundedIcon className="phase_icon emergency" />;
    default:
      return <NoConnectionIcon className="phase_icon no_connection" />;
  }
};

const getPhaseOfFlight = (phaseOfFlight: AircraftStatusPhaseOfFlight): string => {
  switch (phaseOfFlight) {
    case AircraftStatusPhaseOfFlight.stationary:
      return "Parked";
    case AircraftStatusPhaseOfFlight.taxi:
      return "Taxi";
    //case AircraftStatusPhaseOfFlight.takeoff:
    //  return "Departing";
    case AircraftStatusPhaseOfFlight.climb:
      return "Climb";
    case AircraftStatusPhaseOfFlight.cruise:
      return "Cruise";
    case AircraftStatusPhaseOfFlight.descent:
      return "Descent";
    //case AircraftStatusPhaseOfFlight.landing:
    //  return "Arriving";
    case AircraftStatusPhaseOfFlight.emergency:
      return "Emergency";
    default:
      return "Unknown";
  }
};

/**
 * Generate phase of flight message to display in status.
 * @param state - returns an object containing aircraft status state
 * @param airport - aircraft airport location if grounded
 * @returns
 */
const getStateMessage = (state: IAircraftStatusState, airport: string | null, extended?: boolean): string => {
  if (!state?.source) {
    return "";
  }

  const speed = state?.speed ? `${Math.round(state.speed * 0.53996)}kt` : "---kt";
  const location = airport ? `${airport}` : "";
  const altitute = state?.altitude ? `${Math.round(state?.altitude)}ft` : "----ft";
  const phase = state?.phaseOfFlight ? getPhaseOfFlight(state?.phaseOfFlight) : "???";
  const onGround = phase === "Parked" || phase === "Taxiing";

  // Show extended if used inside info bubble (actively selected status)
  switch (true) {
    case extended && onGround:
      return `${phase} / ${location}`;
    case extended && !onGround:
      return `${phase} ${speed} / ${altitute}`;
    case !extended && onGround:
      return `${location}`;
    case !extended && !onGround:
      return `${speed} / ${altitute}`;
    default:
      return "";
  }
};

const aircraftStatusStyles = (theme: Theme, isSelected: boolean, groupColor: string): any => ({
  height: "100%", // set in parent aircraft_status style
  width: "100%", // set in parent aircraft_status style
  color: theme.palette.primary.light,
  backgroundColor: theme.palette.grey[900],
  flexDirection: "column",
  background: isSelected
    ? `linear-gradient(to left, ${theme.palette.background.default}, ${theme.custom.timeline.webglCanvas.selectedRow})`
    : alpha(groupColor, 0.15),
  "&.active": {
    // backgroundColor: theme.palette.grey[800],
  },
  "& .phase_icon": {
    margin: "2px",
    width: 20,
    height: 20,
    "&.no_connection": {
      "& path": { fill: "grey" },
    },
    "&.emergency": {
      "& path": { fill: "red" },
    },
  },
  "& .status_chips": {
    width: 70,
    height: "auto",
    margin: "2px",
    "& .status_chip": {
      width: 12,
      height: 16,
      fontSize: 10,
      margin: "2px 1px",
      borderRadius: 3,
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.grey[800],
      textAlign: "center",
      fontWeight: 900,
      "&.active": {
        backgroundColor: "#00700b", // dark green
      },
    },
  },
  "& .status_message": {
    marginLeft: "4px",
    fontSize: 10,
    height: 15,
    color: theme.palette.grey[400],
  },
  "& .last_received": {
    position: "absolute",
    right: 2,
    bottom: 2,
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: theme.palette.grey[500],
    opacity: 0,
    "&.under5": {
      backgroundColor: theme.palette.success.dark,
      opacity: 1,
    },
    "&.over5": {
      backgroundColor: "#fff200",
      opacity: 1,
    },
    "&.over10": {
      backgroundColor: theme.palette.warning.main,
      opacity: 1,
    },
    "&.over15": {
      backgroundColor: theme.palette.error.main,
      opacity: 1,
    },
    "&.over20": {
      backgroundColor: theme.palette.error.main,
      opacity: 1,
    },
  },
});

const lastUpdateClass = (minSinceLastUpdate: number): string => {
  if (minSinceLastUpdate < 0) return "";
  return minSinceLastUpdate > 20
    ? "over20"
    : minSinceLastUpdate > 15
      ? "over15"
      : minSinceLastUpdate > 10
        ? "over10"
        : minSinceLastUpdate > 5
          ? "over5"
          : "under5";
};

/** Get return minutes since last update. */
const minuteBetweenTimes = (now: Date, lastUpdate: number): number => {
  const millisecondDiff = now?.valueOf() - lastUpdate;
  return millisecondDiff / 60000;
};

export const AircraftStatus = (props: IAircraftStatusProps): JSX.Element => {
  const { windowId, aircraftId, isSelected, groupColor } = props;
  const theme: Theme = useTheme();

  const time = useSelector((state: IRootState) => state.heartbeat.clock);

  const active = false;

  const [minSinceLastUpdate, setMinSinceLastUpdate] = useState<number>(-1);

  const aircraftStatus: IAircraftLastStatus = useSelector(
    (state: IRootState) => state.structures.aircraftStatus[aircraftId]
  );

  // Check the time elapsed since last update and round off to nearest minute.
  useEffect(() => {
    if (!time || !aircraftStatus?.lastUpdated) return;
    const minutes = Math.round(minuteBetweenTimes(time, aircraftStatus?.lastUpdated));
    if (minSinceLastUpdate !== minutes) {
      //TODO Investigate possible performance issues caused by excessive rendering if this changes too often.
      setMinSinceLastUpdate(minutes);
    }
  }, [time, aircraftStatus?.lastUpdated, minSinceLastUpdate]);

  const getVal = (src: any, key: any) => (typeof src?.get === "function" ? src.get(key) : src?.[key]);

  // Aircraft Systems State
  const aircraftState = useMemo(() => {
    const a = aircraftStatus?.aircraft;
    return {
      engineOn: getVal(a, AircraftStateProperties.engine),
      gearDown: getVal(a, AircraftStateProperties.wheels),
      parkBreakOn: getVal(a, AircraftStateProperties.parkBrake),
      mainDoorOpen: getVal(a, AircraftStateProperties.mainDoor),
      cargoDoorOpen: getVal(a, AircraftStateProperties.cargoDoor),
    };
  }, [aircraftStatus?.aircraft]);

  const phaseOfFlight = aircraftStatus?.state?.phaseOfFlight ?? null;
  const airportICAO = aircraftStatus?.airport?.icao ?? null;
  const lastReceived = useMemo(() => {
    return aircraftStatus?.lastUpdated && minSinceLastUpdate !== -1
      ? `Last Status Received: ${formatDateString(
          aircraftStatus.lastUpdated,
          "Etc/UTC",
          true
        )} (${convertMilli2DaysHrsMin(minSinceLastUpdate * 60000)})`
      : "";
  }, [aircraftStatus?.lastUpdated, minSinceLastUpdate]);

  return (
    <Grid container sx={aircraftStatusStyles(theme, isSelected, groupColor)} className={`${active ? "active" : ""}`}>
      <Grid container>
        <Tooltip title={getPhaseOfFlight(phaseOfFlight)} placement="top">
          {getStateIcon(phaseOfFlight)}
        </Tooltip>
        <Grid container className="status_chips">
          {typeof aircraftState.engineOn === "boolean" && (
            <Tooltip title="Engine Running" placement="top">
              <Typography className={`status_chip ${aircraftState.engineOn && "active"}`}>E</Typography>
            </Tooltip>
          )}
          {typeof aircraftState.parkBreakOn === "boolean" && (
            <Tooltip title="Park Brake On" placement="top">
              <Typography className={`status_chip ${aircraftState.parkBreakOn && "active"}`}>P</Typography>
            </Tooltip>
          )}
          {typeof aircraftState.mainDoorOpen === "boolean" && (
            <Tooltip title="Main Door Open" placement="top">
              <Typography className={`status_chip ${aircraftState.mainDoorOpen && "active"}`}>M</Typography>
            </Tooltip>
          )}
          {typeof aircraftState.cargoDoorOpen === "boolean" && (
            <Tooltip title="Cargo Door Open" placement="top">
              <Typography className={`status_chip ${aircraftState.cargoDoorOpen && "active"}`}>C</Typography>
            </Tooltip>
          )}
        </Grid>
      </Grid>
      <Grid container style={{ position: "relative" }}>
        <Typography className="status_message">{getStateMessage(aircraftStatus?.state, airportICAO)}</Typography>
        <Tooltip title={lastReceived} placement="bottom">
          <Grid item className={`last_received ${lastUpdateClass(minSinceLastUpdate)}`}></Grid>
        </Tooltip>
      </Grid>
    </Grid>
  );
};
